import React, { useState, useEffect } from 'react';
import {
    Box,
    Title,
    Text,
    Select,
    Table,
    Paper,
    Loader,
    Alert,
    Modal,
    Button,
    Textarea,
    Badge,
    SimpleGrid,
    Divider,
    Stack,
    Group,
} from '@mantine/core';
import { IconPencil, IconSend } from '@tabler/icons-react';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { getPluralizedLabel, getSingularLabel } from '../../utils/pluralizationUtils';
import { entityService, userService } from '../../services/entityServiceSingleton';
import SchemaCacheService, { FieldOption } from '../../services/SchemaCacheService';
import { LoadingState } from '../common/LoadingState';
import { extractErrorMessage } from '../../utils/apiUtils';
import { debugLog } from '../../utils/debugLog';

interface PatchTestingProps {
    authResolved: boolean;
}

interface EntityItem {
    Id: string;
    [key: string]: any;
}

export const PatchTesting: React.FC<PatchTestingProps> = ({ authResolved }) => {
    const { allEntityTypes, isLoading: entityTypesLoading } = useEntityTypes();
    const [selectedEntityType, setSelectedEntityType] = useState<string>('Person');
    const [entities, setEntities] = useState<EntityItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedEntity, setSelectedEntity] = useState<EntityItem | null>(null);
    const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
    const [jsonContent, setJsonContent] = useState<string>('');
    const [patchLoading, setPatchLoading] = useState(false);
    const [patchError, setPatchError] = useState<string | null>(null);
    const [patchSuccess, setPatchSuccess] = useState<string | null>(null);
    const [schemaFields, setSchemaFields] = useState<FieldOption[]>([]);
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [generatedOperations, setGeneratedOperations] = useState<any[]>([]);

    // Load entities and schema when entity type changes
    useEffect(() => {
        if (authResolved && selectedEntityType) {
            loadEntities();
            loadSchemaFields();
        }
    }, [authResolved, selectedEntityType]);

    // Import the reference type utility
    const { renderEntityProperty } = require('../../utils/referenceTypeUtils');

    // Load schema fields for the selected entity type
    const loadSchemaFields = async () => {
        setSchemaLoading(true);
        try {
            const schemaService = SchemaCacheService.getInstance();
            const fields = await schemaService.getFieldOptions(selectedEntityType);
            setSchemaFields(fields);
            debugLog(`🔍 PatchTesting: Loaded ${fields.length} schema fields for ${selectedEntityType}`);
        } catch (err) {
            console.error('❌ PatchTesting: Error loading schema fields:', err);
            setSchemaFields([]);
        } finally {
            setSchemaLoading(false);
        }
    };

    const loadEntities = async () => {
        setLoading(true);
        setError(null);
        setEntities([]);

        try {
            debugLog(`🔍 PatchTesting: Loading entities for type: ${selectedEntityType}`);

            let response;

            if (selectedEntityType === 'Person') {
                response = await userService.getUsers(1, '', '');
            } else if (selectedEntityType === 'Group') {
                const groupResponse = await entityService.getGroups(1, 25);
                response = { users: groupResponse.groups || [] };
            } else {
                const entityResponse = await entityService.getEntitiesByType(selectedEntityType, 1, 25);
                response = { users: entityResponse.data || [] };
            }

            if (response && response.users) {
                setEntities(response.users);
                debugLog(`✅ PatchTesting: Loaded ${response.users.length} entities`);
            } else {
                setEntities([]);
                debugLog('⚠️ PatchTesting: No entities found');
            }
        } catch (err) {
            console.error('❌ PatchTesting: Error loading entities:', err);
            setError(err instanceof Error ? err.message : 'Failed to load entities');
        } finally {
            setLoading(false);
        }
    };

    const handleEntityTypeChange = (value: string | null) => {
        const newType = value || 'Person';
        debugLog(`🔍 PatchTesting: Entity type changed to: ${newType}`);
        setSelectedEntityType(newType);
    };

    const handleEntityClick = (entity: EntityItem) => {
        debugLog(`🔍 PatchTesting: Entity clicked:`, entity);
        setSelectedEntity(entity);
        setJsonContent(JSON.stringify(entity, null, 2));
        setJsonEditorOpen(true);
        setPatchError(null);
        setPatchSuccess(null);
    };

    const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newContent = event.target.value;
        setJsonContent(newContent);

        if (selectedEntity && newContent.trim()) {
            try {
                const modifiedEntity = JSON.parse(newContent);
                const operations = generatePatchOperations(selectedEntity, modifiedEntity);
                setGeneratedOperations(operations);
            } catch (error) {
                setGeneratedOperations([]);
            }
        } else {
            setGeneratedOperations([]);
        }
    };

    const generatePatchOperations = (originalEntity: any, modifiedEntity: any): any[] => {
        const operations: any[] = [];

        const isEqual = (a: any, b: any): boolean => {
            if (a === b) return true;
            if (a == null || b == null) return false;
            if (typeof a !== typeof b) return false;
            if (Array.isArray(a) && Array.isArray(b)) {
                if (a.length !== b.length) return false;
                return a.every((val, index) => isEqual(val, b[index]));
            }
            if (typeof a === 'object' && typeof b === 'object') {
                const keysA = Object.keys(a);
                const keysB = Object.keys(b);
                if (keysA.length !== keysB.length) return false;
                return keysA.every(key => isEqual(a[key], b[key]));
            }
            return false;
        };

        const getAllPaths = (obj: any, prefix = ''): string[] => {
            const paths: string[] = [];
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const currentPath = prefix ? `${prefix}.${key}` : key;
                    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                        paths.push(...getAllPaths(obj[key], currentPath));
                    } else {
                        paths.push(currentPath);
                    }
                }
            }
            return paths;
        };

        const originalPaths = getAllPaths(originalEntity);
        const modifiedPaths = getAllPaths(modifiedEntity);
        const allPaths = [...new Set([...originalPaths, ...modifiedPaths])];

        for (const path of allPaths) {
            const getNestedValue = (obj: any, p: string): any =>
                p.split('.').reduce((current, key) => current?.[key], obj);

            const originalValue = getNestedValue(originalEntity, path);
            const modifiedValue = getNestedValue(modifiedEntity, path);

            if (originalValue === undefined && modifiedValue !== undefined) {
                operations.push({ op: "add", path: path, value: modifiedValue });
            } else if (originalValue !== undefined && modifiedValue === undefined) {
                const isArrayField = Array.isArray(originalValue) || Array.isArray(modifiedValue);
                if (isArrayField) {
                    operations.push({ op: "remove", path: path });
                } else {
                    operations.push({ op: "replace", path: path, value: null });
                }
            } else if (!isEqual(originalValue, modifiedValue)) {
                operations.push({ op: "replace", path: path, value: modifiedValue });
            }
        }

        return operations;
    };

    const handlePatchSubmit = async () => {
        if (!selectedEntity || !jsonContent) {
            setPatchError('No entity selected or JSON content is empty');
            return;
        }

        setPatchLoading(true);
        setPatchError(null);
        setPatchSuccess(null);

        try {
            const modifiedEntity = JSON.parse(jsonContent);
            const operations = generatePatchOperations(selectedEntity, modifiedEntity);

            const patchRequest = {
                schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"],
                Operations: operations
            };

            debugLog(`🔍 PatchTesting: Generated PATCH operations:`, operations);
            debugLog(`🔍 PatchTesting: Submitting PATCH for entity ${selectedEntity.Id}:`, patchRequest);

            let response;

            if (selectedEntityType === 'Person') {
                response = await userService.patchUser(selectedEntity.Id, patchRequest, selectedEntityType);
            } else if (selectedEntityType === 'Group') {
                response = await entityService.patchEntity(selectedEntity.Id, patchRequest, 'Group');
            } else {
                response = await entityService.patchEntity(selectedEntity.Id, patchRequest, selectedEntityType);
            }

            debugLog(`✅ PatchTesting: PATCH successful:`, response);
            setPatchSuccess('Entity updated successfully!');
            await loadEntities();

        } catch (err) {
            console.error('❌ PatchTesting: PATCH failed:', err);
            const errorMessage = extractErrorMessage(err);
            setPatchError(errorMessage);
        } finally {
            setPatchLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setJsonEditorOpen(false);
        setSelectedEntity(null);
        setJsonContent('');
        setPatchError(null);
        setPatchSuccess(null);
    };

    const blacklistedTypes = [
        'ResourceTypes',
        'Service Provider Configuration',
        'Resource Type'
    ];

    const availableEntityTypes = allEntityTypes.length > 0
        ? [...allEntityTypes]
            .filter(type => !blacklistedTypes.includes(type))
            .sort((a, b) => a.localeCompare(b))
        : ['Person'];

    return (
        <Box p="lg">
            <Title order={1} mb="xs">
                Patch Testing
            </Title>

            <Text size="sm" c="dimmed" mb="lg">
                Select an entity type, choose an entity from the table, and test PATCH operations with custom JSON.
            </Text>

            <SimpleGrid cols={{ base: 1, md: 12 }}>
                {/* Entity Type Selector */}
                <Box style={{ gridColumn: 'span 4' }}>
                    <Paper withBorder p="md">
                        <Title order={5} mb="md">
                            Entity Type
                        </Title>
                        <Select
                            label="Select Entity Type"
                            value={selectedEntityType}
                            onChange={handleEntityTypeChange}
                            data={availableEntityTypes.map((type) => ({
                                value: type,
                                label: getPluralizedLabel(type)
                            }))}
                            disabled={entityTypesLoading}
                        />
                    </Paper>
                </Box>

                {/* Status Information */}
                <Box style={{ gridColumn: 'span 8' }}>
                    <Paper withBorder p="md">
                        <Title order={5} mb="md">
                            Status
                        </Title>
                        <Group gap="xs" wrap="wrap">
                            <Badge variant="outline" color="blue">
                                Type: {getSingularLabel(selectedEntityType)}
                            </Badge>
                            <Badge variant="outline" color="gray">
                                Entities: {entities.length}
                            </Badge>
                            {loading && (
                                <Badge variant="outline" color="cyan" leftSection={<Loader size={14} />}>
                                    Loading...
                                </Badge>
                            )}
                        </Group>
                    </Paper>
                </Box>
            </SimpleGrid>

            <Divider my="lg" />

            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {/* Entities Table */}
            <Paper withBorder p="md">
                <Title order={5} mb="md">
                    {getPluralizedLabel(selectedEntityType)} ({entities.length})
                </Title>

                {loading ? (
                    <LoadingState variant="centered" />
                ) : entities.length === 0 ? (
                    <Alert color="blue">
                        No entities found for {getPluralizedLabel(selectedEntityType)}.
                    </Alert>
                ) : (
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>ID</Table.Th>
                                {schemaFields
                                    .filter(field =>
                                        field.value === 'DisplayName' ||
                                        field.value === 'UserName' ||
                                        field.value === 'name' ||
                                        field.value === 'emails'
                                    )
                                    .slice(0, 3)
                                    .map((field) => (
                                        <Table.Th key={field.value}>
                                            {field.label}
                                        </Table.Th>
                                    ))}
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {entities.map((entity) => (
                                <Table.Tr
                                    key={entity.Id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleEntityClick(entity)}
                                >
                                    <Table.Td>{entity.Id}</Table.Td>
                                    {schemaFields
                                        .filter(field =>
                                            field.value === 'DisplayName' ||
                                            field.value === 'UserName' ||
                                            field.value === 'name' ||
                                            field.value === 'emails'
                                        )
                                        .slice(0, 3)
                                        .map((field) => (
                                            <Table.Td key={field.value}>
                                                {renderEntityProperty(entity[field.value], field.value)}
                                            </Table.Td>
                                        ))}
                                    <Table.Td>
                                        <Button
                                            leftSection={<IconPencil size={16} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEntityClick(entity);
                                            }}
                                            size="xs"
                                            variant="outline"
                                        >
                                            Edit
                                        </Button>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </Paper>

            {/* JSON Editor Modal */}
            <Modal
                opened={jsonEditorOpen}
                onClose={handleCloseDialog}
                title={`Edit ${selectedEntity ? renderEntityProperty(selectedEntity.DisplayName || selectedEntity.UserName || selectedEntity.Id, 'title') : 'Entity'}`}
                size="lg"
            >
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Modify the JSON below and click Submit to send a PATCH request.
                    </Text>

                    {patchError && (
                        <Alert color="red">
                            <Box component="pre" ff="monospace" fz="sm" m={0} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {patchError}
                            </Box>
                        </Alert>
                    )}

                    {patchSuccess && (
                        <Alert color="green">
                            {patchSuccess}
                        </Alert>
                    )}

                    <Textarea
                        minRows={20}
                        value={jsonContent}
                        onChange={handleJsonChange}
                        placeholder="Enter JSON content..."
                        styles={{
                            input: {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem'
                            }
                        }}
                    />

                    {generatedOperations.length > 0 && (
                        <Box mt="md">
                            <Title order={5} mb="xs">
                                Generated PATCH Operations ({generatedOperations.length})
                            </Title>
                            <Paper p="md" bg="var(--mantine-color-default-hover)">
                                <Box component="pre" ff="monospace" fz="sm" m={0} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify({
                                        schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"],
                                        Operations: generatedOperations
                                    }, null, 2)}
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </Stack>

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePatchSubmit}
                        loading={patchLoading}
                        leftSection={<IconSend size={16} />}
                        disabled={!jsonContent.trim()}
                    >
                        {patchLoading ? 'Submitting...' : 'Submit PATCH'}
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
};
